import React, { useState, useEffect } from 'react';
import {
  Input,
  Card,
  CardBody,
  Button,
  Spinner
} from '@heroui/react';
import { Search, MapPin } from 'lucide-react';

const AreaSearch = ({ onAreaSelected, placeholder = "Search for your area..." }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setAreas([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchAreas(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchAreas = async (query) => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/v1/search/areas?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAreas(data.areas || []);
      } else {
        console.error('Failed to search areas');
        setAreas([]);
      }
    } catch (error) {
      console.error('Error searching areas:', error);
      setAreas([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    setSearchQuery(area.full_name);
    setAreas([]);

    if (onAreaSelected) {
      onAreaSelected(area);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startContent={<Search className="w-4 h-4 text-gray-400" />}
          endContent={isLoading && <Spinner size="sm" />}
          className="mb-2"
        />

        {/* Search Results */}
        {areas.length > 0 && (
          <Card className="absolute top-full left-0 right-0 z-50 max-h-60 overflow-y-auto">
            <CardBody className="p-0">
              {areas.map((area) => (
                <button
                  key={area.id}
                  onClick={() => handleAreaSelect(area)}
                  className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <div className="font-medium">{area.name}</div>
                    <div className="text-sm text-gray-500">
                      {area.city}, {area.state}
                    </div>
                  </div>
                </button>
              ))}
            </CardBody>
          </Card>
        )}

        {/* No results message */}
        {searchQuery.length >= 2 && !isLoading && areas.length === 0 && (
          <Card className="absolute top-full left-0 right-0 z-50">
            <CardBody className="p-4 text-center text-gray-500">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No areas found for "{searchQuery}"</p>
              <p className="text-sm mt-1">Try searching for a city or region in Brasil</p>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Selected area confirmation */}
      {selectedArea && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <MapPin className="w-4 h-4" />
            <span className="font-medium">Selected: {selectedArea.full_name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaSearch;
